import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleNavigation } from '@/utils/navigation-utils';
import { cn } from '../../lib/utils';
import { useTenancy, useKeyDocuments, useRegisterItems } from '@/contexts/TenancyContext';
import { useWhiteLabel } from '@/contexts/WhiteLabelContext';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from '@/components/ui/sidebar';

import { TenancySwitcher } from '@/components/TenancySwitcher';
import { SidebarOnboarding } from '@/onboarding/SidebarOnboarding';
import { Home as HomeBlock } from '@/components/sidebar/home';
import { PolicyManagement } from '@/components/sidebar/policy-management';
import { ManagementSystem } from '@/components/sidebar/management-system';
import { Reporting } from '@/components/sidebar/reporting';
import { AdminTools } from '@/components/sidebar/admin-tools';
import { NavSecondary } from '@/components/sidebar/nav-secondary';
import { getSidebarConfig } from '@/config/sidebar';
import { applyWhiteLabelNavigation, getSectionDisplayOrder } from '@/utils/apply-whitelabel-navigation';
import userService from '@/services/userService';
import { toast } from 'sonner';
import { TOUR_STEP_IDS } from "@/lib/tour-constants";
import { AuditorExportDialog } from '@/components/auditor-export/auditor-export-dialog';
import { useSidebar } from '@/components/ui/sidebar';

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  currentRoute?: string;
}

export function AppSidebar({ currentRoute, ...props }: AppSidebarProps) {
  const navigate = useNavigate();
  const { tenancies, activeTenancy } = useTenancy(); // Get tenancies from TenancyContext
  const { keyDocumentItems } = useKeyDocuments(); // Now pulled from unified context
  const { registerItems } = useRegisterItems();   // Now pulled from unified context
  const { toggleSidebar, state } = useSidebar();
  const { config: whiteLabelConfig, logo: whiteLabelLogo } = useWhiteLabel();
  
  // Get sidebar background color from white-label config or default
  const sidebarBgColor = whiteLabelConfig?.sidebarBackground || '#585555';
  
  // Get logo size settings from white-label config
  const getLogoHeight = () => {
    if (whiteLabelConfig?.sidebarLogoSize === 'hidden') return 0;
    if (whiteLabelConfig?.sidebarLogoHeight) return whiteLabelConfig.sidebarLogoHeight;
    
    // Preset sizes
    switch (whiteLabelConfig?.sidebarLogoSize) {
      case 'small': return 32;
      case 'large': return 64;
      case 'medium':
      default: return 48;
    }
  };
  
  const logoHeight = getLogoHeight();
  const isLogoHidden = whiteLabelConfig?.sidebarLogoSize === 'hidden';

  const plan = activeTenancy?.plan || 'default';
  const isIsoPlan = plan === 'default' || plan === 'Business Plan' || plan === 'Enterprise Plan';

  // DED-1511: Track SMB1001 level for Starter plan Audit Centre visibility
  const [smb1001Level, setSmb1001Level] = useState<string>('');

  // Fetch SMB1001 level from company settings
  useEffect(() => {
    const fetchSmb1001Level = async () => {
      if (!activeTenancy?.id) return;
      
      const isStarterPlan = plan === 'starter' || plan === 'Starter' || plan === 'Starter Plan';
      if (!isStarterPlan) return; // Only needed for Starter plan
      
      try {
        const { db } = await import('@/lib/firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const complianceModulesRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'complianceModules');
        const complianceModulesDoc = await getDoc(complianceModulesRef);
        
        if (complianceModulesDoc.exists()) {
          const data = complianceModulesDoc.data();
          // Check if SMB1001 is enabled
          const isEnabled = data?.smb1001?.Enabled === true || data?.SMB1001?.Enabled === true;
          const level = isEnabled ? (data?.smb1001?.Level || data?.SMB1001?.Level || '') : '';
          setSmb1001Level(level);
        }
      } catch (error) {
        console.error('[AppSidebar] Error fetching SMB1001 level:', error);
      }
    };
    
    fetchSmb1001Level();
  }, [activeTenancy?.id, plan]);

  // DED-1511: Listen for SMB1001 level changes from ModulesSection
  useEffect(() => {
    const handleSmb1001Change = (event: CustomEvent<{ level: string; enabled: boolean }>) => {
      setSmb1001Level(event.detail.enabled ? event.detail.level : '');
    };

    window.addEventListener('smb1001LevelChanged', handleSmb1001Change as EventListener);
    return () => {
      window.removeEventListener('smb1001LevelChanged', handleSmb1001Change as EventListener);
    };
  }, []);

  // Dialog state for Auditor Export
  const [isAuditorExportOpen, setIsAuditorExportOpen] = useState(false);

  // Callback to open the auditor export dialog
  const openAuditorExport = () => {
    setIsAuditorExportOpen(true);
  };

  // Only use customerPlan for sidebar config, do NOT pass to DOM or components
  const baseSidebarConfig = getSidebarConfig(plan, keyDocumentItems, registerItems, openAuditorExport, currentRoute, smb1001Level);
  
  // Apply white-label navigation configuration to filter and reorder sections
  const sidebarConfig = applyWhiteLabelNavigation(baseSidebarConfig, whiteLabelConfig?.sidebarNavigation);
  
  // Get section display order based on white-label config
  const sectionOrder = getSectionDisplayOrder(whiteLabelConfig?.sidebarNavigation);

  // Update breadcrumb plan based on tenancy plan
  useEffect(() => {
    const breadcrumbPlan = document.getElementById('breadcrumbPlan');
    if (breadcrumbPlan) {
      breadcrumbPlan.innerText = isIsoPlan ? 'ISO 27001' : 'Privacy Act & E8';
    }
  }, [activeTenancy]);

  // --- Persist sidebar width and collapse state to user profile ---

  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      if (typeof window === 'undefined') return 250;
      const ls = localStorage.getItem('sidebarWidth');
      if (ls) {
        const n = parseInt(ls, 10);
        if (!isNaN(n)) return n;
      }
      const cookie = document.cookie.split('; ').find(r => r.startsWith('sidebar:width='));
      if (cookie) {
        const val = decodeURIComponent(cookie.split('=')[1]);
        if (val.endsWith('rem')) {
          const rem = parseFloat(val);
          const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          return Math.round(rem * root);
        }
        if (val.endsWith('px')) {
          const px = parseInt(val, 10);
          if (!isNaN(px)) return px;
        }
        const num = parseInt(val, 10);
        if (!isNaN(num)) return num;
      }
    } catch (e) {}
    return 250;
  });
  // const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

  // On mount, fetch user profile and initialize sidebar state
  useEffect(() => {
    (async () => {
      const userInfo = await userService.getUserInfo();
      if (userInfo) {
        const profileSidebarWidth = (userInfo as any)?.sidebarWidth;
        if (typeof profileSidebarWidth === 'number' && profileSidebarWidth !== sidebarWidth) {
          setSidebarWidth(profileSidebarWidth);
          try {
            localStorage.setItem('sidebarWidth', String(profileSidebarWidth));
          } catch {}
          try {
            const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
            const remVal = (profileSidebarWidth / rootSize).toFixed(2) + 'rem';
            document.cookie = `sidebar:width=${remVal}; path=/; max-age=${60 * 60 * 24 * 365}`;
          } catch {}
        }
        // if (typeof userInfo.sidebarCollapsed === 'boolean') {
        //   setSidebarCollapsed(prev => prev !== userInfo.sidebarCollapsed ? userInfo.sidebarCollapsed : prev);
        // }
      }
    })();
  }, []);

  // useEffect(() => {
  //   console.log(`[AppSidebar] Sidebar state: ${JSON.stringify(state)}`);
  //   if (state === "expanded") {
  //     setSidebarCollapsed(false);
  //   } else if (state === "collapsed") {
  //     setSidebarCollapsed(true);
  //   }

  // }, [state])

  // Save sidebar width to user profile
  const persistSidebarWidth = async (width: number) => {
    setSidebarWidth(width);
    try {
      localStorage.setItem('sidebarWidth', String(width));
    } catch {}
    try {
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const remVal = (width / rootSize).toFixed(2) + 'rem';
      document.cookie = `sidebar:width=${remVal}; path=/; max-age=${60 * 60 * 24 * 365}`;
    } catch {}
    await userService.saveUserInfo({ sidebarWidth: width });
    toast.success('Sidebar width saved');
  };

  // Save collapse state to user profile
  // const persistSidebarCollapsed = async (collapsed: boolean) => {
  //   setSidebarCollapsed(collapsed);
  //   await userService.saveUserInfo({ sidebarCollapsed: collapsed });
  //   toast.success('Sidebar collapse state saved');
  // };

  // Watch for width changes (from drag, etc)
  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
    const root = document.querySelector(':root') as HTMLElement | null;
    if (root) root.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
  }, [sidebarWidth]);

  // // Watch for collapse state changes
  // useEffect(() => {
  //   // This can be used to trigger UI changes if needed
  //   // Collapse sidebar

  // }, [sidebarCollapsed]);

  // Sidebar config built dynamically based on plan and context data
  // (customerPlan is not passed to DOM)


  return (
    <SidebarProvider
      persistSidebarWidth={persistSidebarWidth}
      // persistSidebarCollapsed={persistSidebarCollapsed}
      // defaultOpen={!sidebarCollapsed}
      style={{ ['--sidebar-width' as any]: `${sidebarWidth}px` }}
      data-sentry-unmask
    >
      <Sidebar collapsible="icon" {...props} data-sentry-unmask>
        {/* Drag rail for resizing the sidebar */}
        <SidebarRail data-sentry-unmask />
        <SidebarHeader 
          className="flex flex-col items-center justify-center p-2" 
          style={{ backgroundColor: sidebarBgColor }}
          data-sentry-unmask
        >
          {!isLogoHidden && (
            <img
              src={whiteLabelLogo || "/assets/img/large_logo.png"}
              alt={whiteLabelConfig?.appName || "Logo"}
              className="cursor-pointer"
              style={{ 
                height: `${logoHeight}px`,
                width: 'auto',
                maxWidth: '80%',
                objectFit: 'contain'
              }}
              onClick={(e) => {
                // Mimic clicking the HomeBlock's first item
                if (sidebarConfig.home && sidebarConfig.home.length > 0) {
                  handleNavigation(sidebarConfig.home[0].url, navigate, e);
                }
              }}
              data-sentry-unmask
            />
          )}
          <TenancySwitcher tenancies={tenancies} data-sentry-unmask />
          <SidebarOnboarding data-sentry-unmask />
        </SidebarHeader>
        <SidebarContent id={TOUR_STEP_IDS.SIDEBAR_MENU} 
          className="text-white" 
          style={{ backgroundColor: sidebarBgColor }}
          data-sentry-unmask
        >
          {/* Dynamically render sections in white-label configured order */}
          {sectionOrder.map((sectionKey) => {
            const items = sidebarConfig[sectionKey];
            if (!items || items.length === 0) return null;

            switch (sectionKey) {
              case 'home':
                return <HomeBlock key={sectionKey} items={items} data-sentry-unmask />;
              case 'policyManagement':
                return <PolicyManagement key={sectionKey} items={items} data-sentry-unmask />;
              case 'managementSystem':
                return <ManagementSystem key={sectionKey} items={items} data-sentry-unmask />;
              case 'reporting':
                return <Reporting key={sectionKey} items={items} data-sentry-unmask />;
              case 'adminTools':
                return <AdminTools key={sectionKey} items={items} data-sentry-unmask />;
              case 'navSecondary':
                return <NavSecondary key={sectionKey} items={items} className="mt-auto" data-sentry-unmask />;
              default:
                return null;
            }
          })}
        </SidebarContent>
      </Sidebar>
      
      {/* Auditor Export Dialog */}
      <AuditorExportDialog
        isOpen={isAuditorExportOpen}
        onClose={() => setIsAuditorExportOpen(false)}
        onExportComplete={() => {
          setIsAuditorExportOpen(false);
        }}
        onError={(error) => {
          console.error('Export error:', error);
        }}
      />
    </SidebarProvider>
  );
}
